import { useLayoutEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themes_animated);

const valuationData = [
    { asset: 'Sydney CBD Office', low: 820, mid: 950, high: 1080, sector: 'Office' },
    { asset: 'Singapore Grade A', low: 1100, mid: 1280, high: 1450, sector: 'Office' },
    { asset: 'Tokyo Marunouchi', low: 760, mid: 890, high: 1020, sector: 'Office' },
    { asset: 'HK Central Office', low: 1350, mid: 1560, high: 1780, sector: 'Office' },
    { asset: 'Melbourne Industrial', low: 210, mid: 265, high: 310, sector: 'Industrial' },
    { asset: 'Singapore Logistics', low: 320, mid: 390, high: 460, sector: 'Industrial' },
    { asset: 'Tokyo Retail Strip', low: 480, mid: 560, high: 640, sector: 'Retail' },
    { asset: 'Sydney Westfield', low: 540, mid: 620, high: 710, sector: 'Retail' },
];

const capRateData = [
    { date: new Date('2020-01-01'), office: 4.6, industrial: 5.8, retail: 5.3 },
    { date: new Date('2021-01-01'), office: 4.4, industrial: 5.4, retail: 5.0 },
    { date: new Date('2022-01-01'), office: 4.8, industrial: 4.9, retail: 5.2 },
    { date: new Date('2023-01-01'), office: 5.3, industrial: 4.5, retail: 5.6 },
    { date: new Date('2024-01-01'), office: 5.5, industrial: 4.3, retail: 5.8 },
    { date: new Date('2025-01-01'), office: 5.2, industrial: 4.1, retail: 5.5 },
];

const AutomatedValuations = () => {
    const valuationRef = useRef<any>(null);
    const capRef = useRef<any>(null);

    useLayoutEffect(() => {
        const chart = am4core.create('avm-valuation-chart', am4charts.XYChart);
        valuationRef.current = chart;

        chart.data = valuationData;
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'asset';
        categoryAxis.renderer.labels.template.rotation = -30;
        categoryAxis.renderer.labels.template.fontSize = 10;
        categoryAxis.renderer.minGridDistance = 20;

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Estimated Value (USD M)';
        valueAxis.numberFormatter.numberFormat = '#,###';

        const lowSeries = chart.series.push(new am4charts.ColumnSeries());
        lowSeries.dataFields.valueY = 'low';
        lowSeries.dataFields.categoryX = 'asset';
        lowSeries.name = 'Low Estimate';
        lowSeries.columns.template.fill = am4core.color('#b0bec5');
        lowSeries.columns.template.stroke = am4core.color('#b0bec5');
        lowSeries.columns.template.tooltipText = 'Low: USD {valueY}M';

        const midSeries = chart.series.push(new am4charts.ColumnSeries());
        midSeries.dataFields.valueY = 'mid';
        midSeries.dataFields.categoryX = 'asset';
        midSeries.name = 'Mid Estimate';
        midSeries.columns.template.fill = am4core.color('#1D4FA3');
        midSeries.columns.template.stroke = am4core.color('#1D4FA3');
        midSeries.columns.template.tooltipText = 'Mid: USD {valueY}M';

        const highSeries = chart.series.push(new am4charts.ColumnSeries());
        highSeries.dataFields.valueY = 'high';
        highSeries.dataFields.categoryX = 'asset';
        highSeries.name = 'High Estimate';
        highSeries.columns.template.fill = am4core.color('#2E8B57');
        highSeries.columns.template.stroke = am4core.color('#2E8B57');
        highSeries.columns.template.tooltipText = 'High: USD {valueY}M';

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    useLayoutEffect(() => {
        const chart = am4core.create('avm-cap-rate-chart', am4charts.XYChart);
        capRef.current = chart;

        chart.data = capRateData;
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';

        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.title.text = 'Year';
        dateAxis.dateFormats.setKey('year', 'yyyy');
        dateAxis.periodChangeDateFormats.setKey('year', 'yyyy');

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'Implied Cap Rate (%)';
        valueAxis.min = 3;
        valueAxis.max = 7;

        const configs = [
            { field: 'office', label: 'Office', color: '#1D4FA3' },
            { field: 'industrial', label: 'Industrial', color: '#2E8B57' },
            { field: 'retail', label: 'Retail', color: '#D6342F' },
        ];

        configs.forEach(({ field, label, color }) => {
            const s = chart.series.push(new am4charts.LineSeries());
            s.dataFields.valueY = field;
            s.dataFields.dateX = 'date';
            s.name = label;
            s.strokeWidth = 2;
            s.stroke = am4core.color(color);
            s.strokeDasharray = field === 'retail' ? '5,4' : '';
            s.tooltipText = `${label}: {valueY}%`;
            s.bullets.push(new am4charts.CircleBullet());
        });

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    return (
        <div style={{ padding: '24px 0' }}>
            <div style={{ padding: '0 30px', marginBottom: 32 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1c4050', marginBottom: 8 }}>
                    Automated Valuations (AVM)
                </h2>
                <p style={{ color: '#6e7189', fontSize: 14, maxWidth: 720 }}>
                    Model-based valuation estimates for prime APAC assets across three confidence bands,
                    alongside implied capitalisation rate trends by sector.
                </p>
            </div>

            <div style={{ padding: '0 30px', marginBottom: 40 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Asset Valuation Estimates — Low / Mid / High (USD M)
                </h3>
                <div id="avm-valuation-chart" style={{ width: '100%', height: 400 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest AVM Model, MSCI Real Assets (Sample Data)
                </p>
            </div>

            <div style={{ padding: '0 30px' }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>
                    Implied Cap Rate Trend by Sector (%) — 2020 to 2025
                </h3>
                <div id="avm-cap-rate-chart" style={{ width: '100%', height: 360 }} />
                <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    Source: Ascend Invest AVM Model, JLL (Sample Data)
                </p>
            </div>
        </div>
    );
};

export default AutomatedValuations;
